
import React from 'react';
import { Order, Driver, SystemConfig, OperatingContext, SmartSplitSuggestion, UserRole } from '../../types';
import { CostComparator } from '../CostComparator';
import { isScheduledRoutingEligible } from '../../services/logistics/core/scheduled';

const TacticalMap = React.lazy(() => import('../TacticalMap'));

interface Props {
  viewMode: 'triage' | 'map' | 'analytics';
  isMapVisible: boolean;
  onToggleMap: () => void;
  smartSplit: SmartSplitSuggestion;
  orders: Order[];
  drivers: Driver[];
  config: SystemConfig;
  context: OperatingContext;
  focusedOrderId: string | null;
  onFocusOrder: (id: string | null) => void;
  heatmapData: [number, number, number][];
  onOrderLocationUpdate: (orderId: string, lat: number, lng: number) => void;
  onDispatchThirdParty: (orders: Order[]) => void;
  onApplySequential: () => void;
  onAcceptWait: (orders: Order[]) => void;
  onIgnoreWait?: (orders: Order[]) => void;
  onManualAssignment: (orderId: string, type: 'own' | 'third') => void;
  onBatchManualAssignment?: (
    ownIds: string[],
    thirdIds: string[],
    reason?: string,
    ownRouteGroups?: Record<string, string>,
    thirdRouteGroups?: Record<string, string>,
    ownActiveRouteByOrder?: Record<string, string>
  ) => void;
  onDispatchCluster: (orders: Order[], mode: 'own' | 'third', driverId?: string) => void;
  mapDisplayMode?: 'tactical' | 'recon';
  setMapDisplayMode?: (mode: 'tactical' | 'recon') => void;
  // v46.6: Props para gerenciar switch de mapa para StreetView
  userWantsGoogle?: boolean;
  setUserWantsGoogle?: (wants: boolean) => void;
  onRestorePreviousMapProvider?: () => void;
  onApplyStrategy?: (suggestion: SmartSplitSuggestion) => void;
  secondsToNextUpdate?: number;
  openSofiaDecision?: (data: any) => void; // NOVO
  handleConfirmStrategy?: (ids: string[], strategy: string) => void;
  handleConfirmBoomerangStrategy?: (goIds: string[], returnIds: string[]) => void;
  handleIgnoreStrategy?: (ids: string[], strategy: string, reason?: string) => void; // NOVO
  isStrategyIgnored?: (orderId: string, strategy: string) => boolean; // v58.6
  userRole?: UserRole;
}

export const TacticalDeck: React.FC<Props> = ({
  viewMode, isMapVisible, onToggleMap, smartSplit, orders, drivers, config, context, focusedOrderId, onFocusOrder, heatmapData, onOrderLocationUpdate, onDispatchThirdParty, onApplySequential, onAcceptWait, onIgnoreWait, onManualAssignment, onBatchManualAssignment, onDispatchCluster, mapDisplayMode, setMapDisplayMode, userWantsGoogle, setUserWantsGoogle, onApplyStrategy, secondsToNextUpdate, openSofiaDecision, handleConfirmStrategy, handleConfirmBoomerangStrategy, handleIgnoreStrategy, isStrategyIgnored, userRole, onRestorePreviousMapProvider
}) => {
  const scheduledLeadMinutes = config.cardapioWeb?.scheduledRoutingLeadMinutes ?? 45;
  const mapOrders = React.useMemo(() => {
    if (!smartSplit?.routes?.length) return orders;

    const suggestedMetaByOrderId = new Map<string, {
      smartClusterId: string;
      routeSequence: number;
      optimizationOutcome: 'own' | 'third';
      bestProvider?: string;
    }>();

    smartSplit.routes.forEach((route, routeIdx) => {
      const routeClusterId = route.orders?.[0]?.smartClusterId || `cluster_${routeIdx}`;
      route.orders.forEach((routeOrder, orderIdx) => {
        suggestedMetaByOrderId.set(routeOrder.id, {
          smartClusterId: routeClusterId,
          routeSequence: orderIdx,
          optimizationOutcome: route.type,
          bestProvider: route.type === 'third' ? route.assigneeName : undefined
        });
      });
    });

    return orders.map(order => {
      const isReassignableAssigned = order.status === 'assigned' && !order.pickupConfirmed;
      if (!['pending', 'confirmed', 'scheduled_confirmed', 'ready'].includes(order.status) && !isReassignableAssigned) return order;

      const suggestedMeta = suggestedMetaByOrderId.get(order.id);
      if (!suggestedMeta) return order;

      return {
        ...order,
        smartClusterId: order.smartClusterId || suggestedMeta.smartClusterId,
        routeSequence: order.routeSequence ?? suggestedMeta.routeSequence,
        optimizationOutcome: order.optimizationOutcome || suggestedMeta.optimizationOutcome,
        bestProvider: order.bestProvider || suggestedMeta.bestProvider
      };
    });
  }, [orders, smartSplit]);

  const pendingForRouting = orders.filter(o =>
    ['confirmed', 'scheduled_confirmed', 'ready'].includes(o.status) &&
    o.type === 'DELIVERY' &&
    isScheduledRoutingEligible(o, scheduledLeadMinutes)
  );

  const activeDriverRoutes = React.useMemo(() => {
    const byDriverId = new Map<string, { driverId: string; driverName: string; activeOrders: number; isInProgress: boolean }>();

    orders.forEach(order => {
      if (!order.driverId || !['assigned', 'dispatched'].includes(order.status)) return;

      const current = byDriverId.get(order.driverId);
      const driverName = drivers.find(driver => driver.id === order.driverId)?.name || 'Motoboy';
      const isInProgress = order.status === 'dispatched' || !!order.pickupConfirmed;

      if (!current) {
        byDriverId.set(order.driverId, {
          driverId: order.driverId,
          driverName,
          activeOrders: 1,
          isInProgress
        });
        return;
      }

      byDriverId.set(order.driverId, {
        ...current,
        activeOrders: current.activeOrders + 1,
        isInProgress: current.isInProgress || isInProgress
      });
    });

    return Array.from(byDriverId.values()).sort((a, b) => a.driverName.localeCompare(b.driverName));
  }, [orders, drivers]);

  const [isGlassMode, setIsGlassMode] = React.useState(() => {
    return localStorage.getItem('sal_logistics_glass_mode') === 'true';
  });

  // v46.6: Estado para rastrear o mapa anterior ao entrar em modo StreetView
  // Isso permite restaurar o mapa original (Leaflet ou Google) ao sair do recon
  const [previousMapProvider, setPreviousMapProvider] = React.useState<'google' | 'leaflet' | null>(null);

  React.useEffect(() => {
    localStorage.setItem('sal_logistics_glass_mode', isGlassMode.toString());
  }, [isGlassMode]);

  // v46.6: Efeito para gerenciar switch automático para Google Maps no modo recon
  // e restaurar o mapa anterior ao sair
  React.useEffect(() => {
    if (mapDisplayMode === 'recon' && focusedOrderId) {
      // Entrando em modo recon: salvar mapa atual e forçar Google Maps
      setPreviousMapProvider(prev => {
        if (prev === null) {
          // Apenas salvar se ainda não temos um previous provider
          // Usa userWantsGoogle se disponível, senão assume leafet por padrão
          return userWantsGoogle ? 'google' : 'leaflet';
        }
        return prev;
      });
      // Forçar Google Maps temporariamente
      if (setUserWantsGoogle) {
        setUserWantsGoogle(true);
      }
    } else if (mapDisplayMode === 'tactical' && previousMapProvider !== null) {
      // Saindo do modo recon: restaurar mapa anterior
      if (previousMapProvider === 'leaflet' && setUserWantsGoogle) {
        setUserWantsGoogle(false);
      }
      // Limpar o estado
      setPreviousMapProvider(null);
    }
  }, [mapDisplayMode, focusedOrderId, userWantsGoogle, setUserWantsGoogle, previousMapProvider]);

  return (
    <div className={`flex-1 relative flex flex-col min-w-[300px] xl:min-w-[350px] ${isGlassMode ? 'bg-transparent' : 'bg-app-bg'} overflow-hidden ${viewMode === 'map' ? 'flex' : 'hidden md:flex'}`}>

      <div
        className={`absolute z-30 transition-all duration-300 ${isMapVisible ? 'top-0 left-0 bottom-0 w-[300px] shadow-2xl overflow-hidden animate-in slide-in-from-left-full rounded-tr-3xl rounded-br-3xl' : 'inset-0'} pointer-events-auto border-r border-border-dim`}
        style={isGlassMode ? {
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          backgroundColor: 'rgba(var(--glass-panel-bg))'
        } : {
          backgroundColor: config.isSolarMode ? 'rgba(var(--bg-main), 0.95)' : 'rgba(var(--sal-gunmetal), 0.95)'
        }}
      >
        <CostComparator
          suggestion={smartSplit}
          pendingOrders={pendingForRouting}
          onApplySuggestion={() => { if (onApplyStrategy) onApplyStrategy(smartSplit); else if (smartSplit.thirdParty.length > 0) onDispatchThirdParty(smartSplit.thirdParty); }}
          onDispatchAll={(ordersToDispatch, mode) => { if (mode === 'third') onDispatchThirdParty(ordersToDispatch); }}
          onApplySequential={onApplySequential}
          onAcceptWait={onAcceptWait}
          onIgnoreWait={onIgnoreWait}
          onManualAssignment={onManualAssignment}
          onBatchManualAssignment={onBatchManualAssignment}
          isMapVisible={isMapVisible} onToggleMap={onToggleMap} onDispatchRoute={onDispatchCluster} config={config} secondsToNextUpdate={secondsToNextUpdate}
          // Driver Selection
          drivers={drivers}
          // Passando props para o modal de decisão
          openSofiaDecision={openSofiaDecision}
          handleConfirmStrategy={handleConfirmStrategy}
          handleConfirmBoomerangStrategy={handleConfirmBoomerangStrategy}
          handleIgnoreStrategy={handleIgnoreStrategy}
          isStrategyIgnored={isStrategyIgnored}
          userRole={userRole}
          isGlassMode={isGlassMode}
          onToggleGlassMode={() => setIsGlassMode(!isGlassMode)}
          activeDriverRoutes={activeDriverRoutes}
        />
      </div>

      <div className="absolute inset-0 z-0">
        {viewMode === 'map' || isGlassMode ? (
          <React.Suspense fallback={<div className="w-full h-full bg-sal-gunmetal flex items-center justify-center text-slate-700 uppercase font-black text-xs tracking-[0.4em]">Carregando Mapa</div>}>
            <TacticalMap
              orders={mapOrders} drivers={drivers} config={config} context={context}
              focusedOrderId={focusedOrderId} onFocusOrder={onFocusOrder}
              showHeatmap={false} heatmapData={heatmapData}
              onOrderLocationUpdate={onOrderLocationUpdate}
              isVisible={viewMode === 'map' || isGlassMode}
              displayMode={mapDisplayMode} setDisplayMode={setMapDisplayMode}
              // v46.6: Props para gerenciar switch de mapa para StreetView
              userWantsGoogle={userWantsGoogle}
              setUserWantsGoogle={setUserWantsGoogle}
            />
          </React.Suspense>
        ) : (
          <div className="w-full h-full bg-sal-gunmetal flex items-center justify-center text-slate-700 uppercase font-black text-xs tracking-[0.4em]">Monitoramento Ativo</div>
        )}
      </div>
    </div>
  );
};
